import { useEffect, useRef, useState } from "react";
import { useLocalParticipant, useTracks, VideoTrack } from "@livekit/components-react";
import { Track } from "livekit-client";
import { Camera, Mic, MicOff, Video, VideoOff, UserRound } from "lucide-react";

const Tile = ({ ref_, big }) => {
  const p = ref_.participant;
  const live = ref_.publication?.track && !ref_.publication.isMuted;
  return (
    <div data-stage-video="1" className={`relative overflow-hidden rounded-md border border-[#2d2d38] bg-black ${big ? "aspect-video max-h-[620px] w-full" : "aspect-video"}`} data-testid={`stage-tile-${p.identity}`}>
      {live ? <VideoTrack trackRef={ref_} className="h-full w-full object-cover" /> : (
        <div className="h-full w-full grid place-items-center"><UserRound className="h-14 w-14 text-zinc-700" /></div>
      )}
      <div className="absolute left-2 bottom-2 flex items-center gap-1.5 rounded-[3px] bg-black/70 px-2 py-0.5 backdrop-blur">
        <span className={`h-1.5 w-1.5 rounded-full ${p.isMicrophoneEnabled ? "bg-[#39ff6a] glow-green" : "bg-zinc-600"}`} />
        <span className="text-xs">{p.name || "Performer"}{p.isLocal ? " (you)" : ""}</span>
      </div>
    </div>
  );
};

const useInstrumentPublish = (inst, canPublish) => {
  const { localParticipant } = useLocalParticipant();
  useEffect(() => {
    if (!canPublish || !inst) return;
    const track = inst.stream().getAudioTracks()[0];
    localParticipant.publishTrack(track, { name: "instruments", source: Track.Source.ScreenShareAudio })
      .catch((e) => console.warn("Instrument stream publish failed", e));
    return () => { localParticipant.unpublishTrack(track, false).catch((e) => console.warn("Instrument stream unpublish failed", e)); };
  }, [inst, canPublish, localParticipant]);
};

export const LiveStage = ({ engine, inst, stageIds, kind, canPublish, onLocalMic }) => {
  const cams = useTracks([{ source: Track.Source.Camera, withPlaceholder: true }], { onlySubscribed: false });
  const audio = useTracks([Track.Source.Microphone, Track.Source.ScreenShareAudio], { onlySubscribed: true });
  const { localParticipant, isMicrophoneEnabled, isCameraEnabled, microphoneTrack } = useLocalParticipant();
  useInstrumentPublish(inst, canPublish);

  useEffect(() => {
    const keys = new Set();
    audio.forEach((ref) => {
      const t = ref.publication?.track?.mediaStreamTrack;
      if (ref.participant.isLocal || !t) return;
      const key = `${ref.source === Track.Source.ScreenShareAudio ? "i" : "p"}:${ref.participant.identity}`;
      keys.add(key);
      engine.addTrack(key, t);
    });
    engine.keys().forEach((k) => /^[pi]:/.test(k) && !keys.has(k) && !k.endsWith(`:${localParticipant.identity}`) && engine.removeSource(k));
  }, [audio, engine, localParticipant.identity]);

  useEffect(() => () => engine.keys().forEach((k) => k.startsWith("p:") && engine.removeSource(k)), [engine]);
  useEffect(() => { onLocalMic(microphoneTrack?.track?.mediaStreamTrack || null); }, [microphoneTrack, onLocalMic]);

  const tiles = cams.filter((r) => kind === "lesson" || stageIds.includes(r.participant.identity));
  const cls = "flex items-center gap-2 rounded-[3px] border px-3 py-1.5 text-sm transition-colors";
  return (
    <div className="relative" data-testid="live-stage">
      {tiles.length ? (
        <div className={`grid gap-3 ${tiles.length > 1 ? "sm:grid-cols-2" : ""} ${tiles.length > 4 ? "lg:grid-cols-3" : ""}`}>
          {tiles.map((r) => <Tile key={`${r.participant.identity}-${r.source}`} ref_={r} big={tiles.length === 1} />)}
        </div>
      ) : (
        <div className="h-[380px] md:h-[520px] grid place-items-center rounded-md border border-dashed border-zinc-800" data-testid="stage-empty">
          <p className="mono-label">The stage is empty</p>
        </div>
      )}
      {canPublish && (
        <div className="mt-3 flex gap-2">
          <button data-testid="stage-toggle-mic-btn" onClick={() => localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled)}
            className={`${cls} ${isMicrophoneEnabled ? "border-[#39ff6a]/60 text-[#39ff6a]" : "border-zinc-700 text-zinc-400"}`}>
            {isMicrophoneEnabled ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />} Mic
          </button>
          <button data-testid="stage-toggle-camera-btn" onClick={() => localParticipant.setCameraEnabled(!isCameraEnabled)}
            className={`${cls} ${isCameraEnabled ? "border-[#39ff6a]/60 text-[#39ff6a]" : "border-zinc-700 text-zinc-400"}`}>
            {isCameraEnabled ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />} Camera
          </button>
        </div>
      )}
    </div>
  );
};

export const deviceError = (e) => {
  if (e?.name === "NotAllowedError") return "Your browser blocked the camera and mic. Click the camera icon in the address bar, allow access, then try again (if this page is inside a preview frame, open it in its own tab).";
  if (e?.name === "NotFoundError") return "No camera or microphone was found on this computer.";
  if (e?.name === "NotReadableError") return "Another app is using your camera or mic. Close it and try again.";
  return "Couldn't open your camera or mic.";
};

// Local camera + mic check that works even before live video is connected.
const DevicePreview = ({ onLocalMic }) => {
  const [stream, setStream] = useState(null);
  const [err, setErr] = useState("");
  const vid = useRef(null);
  const meter = useRef(null);
  useEffect(() => {
    if (!stream) return;
    vid.current.srcObject = stream;
    const ctx = new AudioContext();
    const an = ctx.createAnalyser();
    an.fftSize = 512;
    const buf = new Float32Array(512);
    if (stream.getAudioTracks().length) ctx.createMediaStreamSource(stream).connect(an);
    let raf;
    const tick = () => {
      an.getFloatTimeDomainData(buf);
      let s = 0;
      for (const v of buf) s += v * v;
      if (meter.current) meter.current.style.width = `${Math.min(100, Math.sqrt(s / buf.length) * 400)}%`;
      raf = requestAnimationFrame(tick);
    };
    tick();
    onLocalMic(stream.getAudioTracks()[0] || null);
    return () => { cancelAnimationFrame(raf); ctx.close(); onLocalMic(null); };
  }, [stream, onLocalMic]);
  useEffect(() => () => stream?.getTracks().forEach((t) => t.stop()), [stream]);

  const start = async () => {
    setErr("");
    try { setStream(await navigator.mediaDevices.getUserMedia({ video: true, audio: true })); } catch (e) { setErr(deviceError(e)); }
  };
  const stop = () => { stream?.getTracks().forEach((t) => t.stop()); setStream(null); };

  if (!stream) {
    return (
      <div className="mt-4">
        <button onClick={start} data-testid="device-test-btn" className="inline-flex items-center gap-2 rounded-[3px] bg-yellow-400 px-4 py-2 text-sm font-bold text-black hover:bg-yellow-300">
          <Camera className="h-4 w-4" /> Test my camera & mic
        </button>
        {err && <p className="mt-3 max-w-md text-sm text-pink-300" data-testid="device-test-error">{err}</p>}
      </div>
    );
  }
  return (
    <div className="mt-4 w-full max-w-3xl" data-testid="device-preview">
      <div data-stage-video="1" className="relative aspect-video overflow-hidden rounded-md border border-[#39ff6a]/50 bg-black">
        <video ref={vid} autoPlay playsInline muted className="h-full w-full object-cover -scale-x-100" data-testid="device-preview-video" />
        <span className="absolute left-2 top-2 rounded-[3px] bg-black/70 px-2 py-0.5 mono text-[10px] text-[#39ff6a]">Preview · only you see this</span>
      </div>
      <div className="mt-2 flex items-center gap-3">
        <Mic className="h-4 w-4 text-[#39ff6a]" />
        <div className="h-2 flex-1 rounded bg-zinc-900 overflow-hidden"><div ref={meter} className="h-full bg-[#39ff6a] transition-[width] duration-75" data-testid="device-mic-meter" /></div>
        <button onClick={stop} data-testid="device-test-stop-btn" className="mono text-[10px] uppercase text-zinc-400 hover:text-white">Stop test</button>
      </div>
    </div>
  );
};

export const OfflineStage = ({ reason, canPublish, onLocalMic }) => (
  <div className="min-h-[380px] md:min-h-[520px] grid place-items-center rounded-md border border-dashed border-zinc-800 bg-black/40 text-center px-6 py-8" data-testid="stage-offline">
    <div className="flex w-full flex-col items-center">
      <VideoOff className="mx-auto h-8 w-8 text-zinc-600" />
      <p className="mt-3 text-sm text-zinc-300" data-testid="stage-offline-reason">{reason}</p>
      <p className="mt-1 mono-label">Chat, tracks, instruments and the mixer still work</p>
      {canPublish && <DevicePreview onLocalMic={onLocalMic} />}
    </div>
  </div>
);
